// FulfillmentToggle — segmented choice between "Ambil sekarang" and "Kirim nanti".
//
// Controls whether stock is deducted immediately at transaction creation
// (fulfillment='now') or held for a later delivery (fulfillment='later').
// Default is 'now'.
//
// One row on purpose. This sits in the cart footer, which shares a fixed
// column with the item list: every line of height here is a line of items the
// cashier cannot see. The two-card version with descriptions cost ~130px and
// left room for two items. The meaning of each option is in its title
// attribute and in the one-line note below.

import { Truck, Hand } from 'lucide-react';
import { cn } from '@/lib/format';
import type { FulfillmentType } from '@/types/domain';

export interface FulfillmentToggleProps {
  value: FulfillmentType;
  onChange: (next: FulfillmentType) => void;
  disabled?: boolean;
}

const OPTIONS: Array<{
  value: FulfillmentType;
  label: string;
  description: string;
  icon: typeof Truck;
}> = [
  {
    value: 'now',
    label: 'Ambil sekarang',
    description: 'Stock langsung dipotong, customer bawa barang hari ini.',
    icon: Hand,
  },
  {
    value: 'later',
    label: 'Kirim nanti',
    description: 'Stock di-hold, dikirim / diambil di lain hari.',
    icon: Truck,
  },
];

export function FulfillmentToggle({
  value,
  onChange,
  disabled = false,
}: FulfillmentToggleProps) {
  return (
    <fieldset className="flex items-center justify-between gap-3">
      <legend className="sr-only">Fulfillment</legend>
      <span className="text-small font-medium text-ink shrink-0">Fulfillment</span>
      <div
        role="radiogroup"
        className="inline-flex p-1 rounded bg-canvas border border-line"
      >
        {OPTIONS.map((opt) => {
          const Icon = opt.icon;
          const active = value === opt.value;
          return (
            <button
              key={opt.value}
              type="button"
              role="radio"
              aria-checked={active}
              onClick={() => onChange(opt.value)}
              disabled={disabled}
              title={opt.description}
              className={cn(
                'inline-flex items-center gap-1.5 px-3 py-1.5 rounded text-caption font-medium transition-colors',
                active ? 'bg-surface text-ink shadow-hover' : 'text-ink-subtle hover:text-ink',
                disabled && 'opacity-50 cursor-not-allowed',
              )}
            >
              <Icon size={12} />
              {opt.label}
            </button>
          );
        })}
      </div>
    </fieldset>
  );
}
